import axios from "axios";
import { message } from "antd";
import { randomID } from "./randomId";
import { globalModel } from "../global/GlobalModel";

export interface Request<T> {
  requestId: string;
  traceId: string;
  token: string;
  parameter: T;
}

export interface Response<T> {
  success: boolean;
  result: T;
  costTime: number;
  errorCode: string;
  errorMessages: string | string[];
}

const isDateString = (value: unknown): boolean =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(value);

const convertDates = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(convertDates);
  if (obj && typeof obj === "object") {
    const o = obj as Record<string, unknown>;
    for (const key of Object.keys(o)) {
      if (isDateString(o[key])) {
        o[key] = new Date(o[key] as string);
      } else if (typeof o[key] === "object") {
        o[key] = convertDates(o[key]) as unknown;
      }
    }
  }
  return obj;
};

const formatDateToString = (date: Date): string => {
  if (!date) return "未知";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  const s = String(date.getSeconds()).padStart(2, "0");
  return `${y}-${m}-${d} ${h}:${min}:${s}`;
};

const convertDatesToString = (obj: unknown): unknown => {
  if (Array.isArray(obj)) return obj.map(convertDatesToString);
  if (obj && typeof obj === "object") {
    const o = obj as Record<string, unknown>;
    for (const key of Object.keys(o)) {
      if (o[key] instanceof Date) {
        o[key] = formatDateToString(o[key] as Date);
      } else if (typeof o[key] === "object") {
        o[key] = convertDatesToString(o[key]) as unknown;
      }
    }
  }
  return obj;
};

const normalizeErrorMessage = (
  errorMessages: string | string[] | undefined,
  fallback: string
): string => {
  if (Array.isArray(errorMessages)) {
    const first = errorMessages.find(
      (v) => typeof v === "string" && v.trim().length > 0
    );
    return first ?? fallback;
  }
  if (typeof errorMessages === "string" && errorMessages.trim().length > 0) {
    return errorMessages;
  }
  return fallback;
};

const rawApiDomain = process.env.REACT_APP_API_DOMAIN ?? "";
/** 需含协议；若漏写则补 http://，避免被当成相对路径拼到 /space/:id/... 下 */
export const API_DOMAIN =
  rawApiDomain && !/^https?:\/\//i.test(rawApiDomain)
    ? `http://${rawApiDomain.replace(/^\/+/, "")}`
    : rawApiDomain;

export const post = async <T, R>(
  url: string,
  inputParameter: T,
  options?: { silent?: boolean }
): Promise<R | string> => {
  let status = false;
  let result: R | null = null;
  let errorMessage = "";
  let errorCode = "";

  try {
    const traceId = randomID();
    const token = localStorage.getItem("token");
    const param = convertDatesToString(inputParameter) as T;
    const requestData: Request<T> = {
      requestId: traceId,
      traceId,
      token: token ?? "",
      parameter: param,
    };

    const response = await axios.post<Response<R>>(
      `${API_DOMAIN}${url}`,
      requestData
    );
    status = response.data.success;

    if (status) {
      result = convertDates(response.data.result) as R;
    } else {
      errorCode = response.data.errorCode;
      if (errorCode === "0000111") {
        globalModel.openLoginModal();
      }
      errorMessage = normalizeErrorMessage(
        response.data.errorMessages,
        errorCode || "请求失败"
      );
    }
  } catch (e: unknown) {
    const axiosError = e as {
      response?: { data?: Response<R> };
    };
    const data = axiosError?.response?.data;
    if (data) {
      errorCode = data.errorCode || errorCode;
      errorMessage = normalizeErrorMessage(
        data.errorMessages,
        errorCode || "请求失败"
      );
      if (errorCode === "0000111") {
        globalModel.openLoginModal();
      }
    } else {
      errorMessage = "网络异常，请稍后重试";
    }
  }

  if (status) {
    return result as R;
  }
  if (!options?.silent) {
    message.error(errorMessage || errorCode || "请求失败");
  }
  return errorCode || errorMessage || "请求失败";
};
